<template>
  <v-container
    id="classesView"
  >
    <v-overlay class="waitoverlay" :value="busy">
     <v-progress-circular
      indeterminate
      size="64"
      class="mr-4"
    /> {{ $t('global.loading') }}
    </v-overlay>
    <div class="info-wrapper">
      <v-icon class="info-icon">mdi-information-outline</v-icon>
      <span class="info-text">
        {{ $t('classes.banner-text') }} </span>
    </div>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12" sm="8" md="6">
            {{ $t('classes.your-classes') }} <v-spacer />
          </v-col>
          <v-col cols="12" sm="4" md="6" class="text-sm-right">
            <v-btn color="primary" @click="createItemDialog">
              {{ $t('classes.new-class') }}
            </v-btn>
            <v-btn color="secondary" :aria-label="$t('classes.refresh')" @click="refreshItems">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <div class="searchBox">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('global.search')"
            :aria-label="$t('global.text-field')+' '+$t('global.search') +' '+ $t('classes.classes')"
            single-line
            hide-details
          />
        </div>
        <v-data-table
          v-model="selected"
          filterable
          :headers="headers.map((x) => { return { ...x, text: $t(x.text) } })"
          :items="cleanItems"
          item-key="id"
          :loading="loading"
          :search="search"
          :items-per-page="30"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
        >
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon
                  v-if="isOwner(item)"
                  class="mr-2"
                  :aria-label="$t('global.edit') + ' ' + $t('classes.classParam', [item.displayName])"
                  v-on="on"
                  @click="editItem(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-icon
                  v-if="isOwner(item)"
                  :aria-label="$t('global.delete') + ' ' + $t('classes.classParam', [item.displayName])"
                  v-on="on"
                  @click="deleteItem(item)"
                >
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t('delete') }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.name="{ item }">
            <a :href="createURL(item)">
              {{ item.name }}
            </a>
          </template>
          <template v-slot:item.creator="{ item }">
            {{ item.creator.displayName }}
          </template>
          <template v-slot:item.content="{ item }">
            {{ contentDisplayName(item.content) }}
          </template>
          <template v-slot:item.created="{ item }">
            {{ displayTime(item.created) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-row justify="center">
      <v-dialog
        v-model="showEditDialog"
        max-width="600px"
        persistent
      >
        <class-edit
          :model="editedItem"
          :editors="coaches"
          :content="content"
          :classes="items"
          @cancel="closeEditDialog"
          @save="saveEditDialog"
        />
      </v-dialog>
       <v-dialog v-model="showDeleteDialog" max-width="500px">
          <v-card>
            <v-card-title>{{ $t('global.confirm') }}</v-card-title>
            <v-card-text>{{ $t('classes.confirm-delete') }} <span class="font-weight-bold">{{ selectedClassName }}</span>?</v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="blue" @click="closeDeleteDialog">
{{ $t('global.no') }}
</v-btn>
              <v-btn color="red" @click="confirmDeleteItem">
{{ $t('global.yes') }}
</v-btn>
              <v-spacer />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- error snackbar -->
        <v-snackbar v-model="error">
          {{ errorMessage }}
          <v-btn
            color="primary"
            text
            @click="error = false"
          >
            {{ $t('global.close') }}
          </v-btn>
        </v-snackbar>
    </v-row>
  </v-container>
</template>

<script>

  import api from '@/helpers/api.js'
  import classCreate from './ClassEditDialog.vue'
  import 'firebase/auth'
  import { Users } from '@/helpers/users.js'
  import moment from 'moment'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'

  export default {
    name: 'Main',

    components: {
      'class-edit': classCreate,
    },

    data () {
      return {
        error: false,
        errorMessage: '',
        sortBy: 'created',
        sortDesc: true,
        loading: false,
        busy: false,
        showEditDialog: false,
        showDeleteDialog: false,
        editedIndex: -1,
        editedItem: {},
        coaches: [],
        // content: [],
        itemsPerPage: 30,
        defaultItem: {
          name: '',
          content: '',
          id: undefined,
          learnerCount: 0,
          completionRate: 0,
          editors: [],
          enrolmentEndsAt: null,
          enrolmentType: 'public',
          permittedDomain: [],
          email: null,
          notificationEmail: null,
          allowSelfRegistration: false,
          allowGuestJoin: false,
        },
        search: '',
        selected: [],
        items: [],
        headers: [
          {
            text: 'classes.headers.name',
            align: 'start',
            sortable: true,
            value: 'name',
          },
          { text: 'classes.headers.assigned-module', value: 'content' },
          { text: 'classes.headers.members', value: 'learnerCount' },
          { text: 'classes.headers.created', value: 'created' },
          { text: 'classes.headers.creator', value: 'creatorName' },
          { text: 'classes.headers.actions', value: 'actions', sortable: false },
        ],
      }
    },

    computed: {
      cleanItems () {
        // console.log('cleanItems = ' + JSON.stringify(this.items))
        return this.items === null ? [] : this.items
      },
      selectedClassName () {
        return (this.editedItem) ? this.editedItem.name : ''
      },
      content () {
        return this.$store.getters.allPurchasedModules ?? []
      },
    },

    async mounted () {
      this.loading = true
      const promises = [
        ContentLibrary.Get(this.$store, this.$i18n),
        this.refreshItems(),
        this.refreshStatics(),
      ]
      await Promise.all(promises)
      this.loading = false
    },

    methods: {
      showError (message) {
        this.error = true
        this.errorMessage = message
      },
      ratioToPercentage (item) {
        return item !== undefined ? Math.floor(item * 100) + '%' : 'N/A'
      },
      displayTime (time) {
        // console.log(time.seconds)
        return moment(time).format('HH:mm DD/MM/YY')
      },
      createURL (item) {
        return '#/admin/classes/' + item.id
      },
      isOwner: function (item) {
        // console.log('isOwner = ' + JSON.stringify(item))
        return !item.creator || !item.creator.id || item.creator.id === this.$store.getters.currentUserProfile.user.id
      },
      contentDisplayName (contentID) {
        if (this.$store.state.contentLibrary === null) return ''
      
        if (!contentID || contentID.length === 0) {
          return this.$t('classes.no-module')
        }
        if (Array.isArray(contentID) && contentID.length > 1) {
          return this.$t('classes.multiple-modules', { count: contentID.length })
        }
        if (Array.isArray(contentID)) contentID = contentID[0]

        // this.$t('modules.' + contentID.split('(')[0])
        return ContentLibrary.NameOf(this.$store, this.$i18n.locale, contentID.split('(')[0])
      },
      async createItemDialog () {
        this.busy = true
        await this.$store.dispatch('fetchContent')
        this.busy = false
        this.editedIndex = -1
        this.editedItem = Object.assign({}, this.defaultItem)
        this.showEditDialog = true
      },
      async refreshItems () {
        this.loading = true
        const classes = await api.getClasses()
        // console.log(classes)
        this.items = classes.classes === null ? [] : classes.classes
        this.loading = false
      },
      async refreshStatics () {
        this.coaches = await Users.Get(['coach'])
        const userInArrayIndex = this.coaches.findIndex(x => x.id === this.$store.getters.currentUserProfile.user.id)
        if (userInArrayIndex >= 0) {
          this.coaches.splice(userInArrayIndex, 1)
        }
      },
      async editItem (item) {
        this.busy = true
        // console.log(`edit item ${item.id}`)
        this.editedIndex = this.items.indexOf(item)
        const editItem = await api.getClass(item.id)
        this.editedItem = editItem
        this.editedItem.id = item.id
        this.showEditDialog = true
        this.busy = false
      },
      modifyDomainObjects (domains) {
        return domains.map((domain) => {
          return {
            text: domain,
            color: 'gray',
          }
        })
      },
      closeEditDialog () {
        this.showEditDialog = false
      },
      async saveEditDialog (newVal) {
        this.showEditDialog = false
        this.busy = true
        if (this.editedIndex === -1) {
          try {
            const id = await api.createClass(newVal)
            this.editedItem.id = id
          } catch (ex) {
            this.showError(ex.message) 
            this.busy = false
          }
          await this.refreshItems()
        }
        else {
          await api.updateClass(newVal.id, newVal)
          Object.assign(this.items[this.editedIndex], newVal)
        }
        this.busy = false
      },
      async confirmDeleteItem () {
        this.closeDeleteDialog()
        this.busy = true
        this.items.splice(this.editedIndex, 1)
        await api.deleteClass(this.editedItem.id)
        this.busy = false
      },
      deleteItem (item) {
        this.editedItem = item
        this.editedIndex = this.items.indexOf(item)
        this.showDeleteDialog = true
      },
      closeDeleteDialog () {
        this.showDeleteDialog = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
        })
      },
    },

  }
</script>

<style scoped>
.info-wrapper {
  display: flex;
  align-items: center; /* Center align items vertically */
  gap: 8px; /* Space between the icon and text */
  flex-wrap: nowrap; /* Prevent wrapping to keep the span to the right of the icon */
  border: 1px solid #ccc; /* Optional: to highlight the div's boundary */
  padding: 8px; /* Optional: spacing inside the div */
  max-width: 100%; /* Ensures it doesn't overflow */
  color: white;
  font-size: 16px;
  font-weight: 400;
  background-color: #1B3148;
  padding: 15px;
  line-height: 1.2em;
  margin-bottom: 1em;
  margin-top: 1em;
  border-style: solid;
  border-width: 1px;
  border-radius: .5em;
}

.info-icon {
  flex-shrink: 0; /* Prevents the icon from shrinking on wrapping */
  font-size: 24px; /* Adjust the size of the PrimeVue icon */
  color: #ffffff; /* Optional: change icon color */
  padding-right: 5px;
}

.info-text {
  flex-grow: 1; /* Allows the text to take up remaining space */
  word-wrap: break-word; /* Ensures long text breaks gracefully */
  font-size: 16px; /* Adjust font size for readability */
  overflow-wrap: break-word; /* Ensures long words break gracefully */
}
</style>
